import { mapActions, mapGetters } from 'vuex'
import MarkdownEditor from '@/components/MarkdownEditor/MarkdownEditor.vue'

const STATUS_OPTIONS = ['no status', 'in progress', 'done', 'paused']

const PAID_STATUS_ITEMS = [
  { text: 'No status', value: 'no status', color: 'grey lighten-4', textColor: 'grey darken-2', icon: 'mdi-circle-outline' },
  { text: 'In progress', value: 'in progress', color: 'blue lighten-5', textColor: 'blue darken-3', icon: 'mdi-progress-clock' },
  { text: 'Done', value: 'done', color: 'green lighten-5', textColor: 'green darken-3', icon: 'mdi-check-circle-outline' }
]

const PIPELINE_STATUS_ITEMS = [
  { text: 'Lead', value: 'no status', color: 'grey lighten-4', textColor: 'grey darken-2', icon: 'mdi-account-outline' },
  { text: 'Hot lead', value: 'in progress', color: 'orange lighten-5', textColor: 'orange darken-4', icon: 'mdi-fire' },
  { text: 'Paused', value: 'paused', color: 'blue-grey lighten-5', textColor: 'blue-grey darken-3', icon: 'mdi-pause-circle-outline' }
]

const LOST_STATUS_ITEMS = [
  { text: 'Lost', value: 'no status', color: 'red lighten-5', textColor: 'red darken-3', icon: 'mdi-close-circle-outline' },
  { text: 'Paused', value: 'paused', color: 'blue-grey lighten-5', textColor: 'blue-grey darken-3', icon: 'mdi-pause-circle-outline' }
]

const CLIENT_STATUS_ITEMS = [
  { text: 'Paid', value: 'paid', icon: 'mdi-check-circle-outline' },
  { text: 'Pipeline', value: 'pipeline', icon: 'mdi-filter-outline' },
  { text: 'Lost', value: 'lost', icon: 'mdi-close-circle-outline' }
]

const CMS_META = {
  none: { color: 'grey lighten-3', textColor: 'grey darken-2' },
  wordpress: { color: 'blue lighten-5', textColor: 'blue darken-3' },
  webflow: { color: 'teal lighten-5', textColor: 'teal darken-3' },
  shopify: { color: 'green lighten-5', textColor: 'green darken-3' },
  autoblog: { color: 'amber lighten-4', textColor: 'amber darken-4' },
  'custom-cms': { color: 'deep-purple lighten-5', textColor: 'deep-purple darken-2' },
  magento: { color: 'orange lighten-5', textColor: 'orange darken-4' },
  notion: { color: 'blue-grey lighten-4', textColor: 'blue-grey darken-3' }
}

export default {
  name: 'AdminContentplansOverview',
  components: {
    MarkdownEditor
  },
  data () {
    return {
      isLoading: {
        overview: false
      },
      selectedTab: 'all',
      selectedClientScope: 'paid',
      clientStatusItems: CLIENT_STATUS_ITEMS,
      nameSearch: '',
      rows: [],
      savingRows: {},
      workspaceActionLoading: {},
      notesDialog: {
        open: false,
        saving: false,
        rowId: null,
        draft: ''
      },
      addTargetDialog: {
        open: false,
        saving: false,
        companyName: '',
        domain: '',
        mainContacts: '',
        invoiceSent: false,
        discountPercentInput: '',
        followupDate: '',
        priceInput: '',
        notes: ''
      },
      moveDialog: {
        open: false,
        saving: false,
        row: null,
        clientStatus: '',
        date: '',
        lostReason: ''
      },
      baseHeaders: [
        { text: 'Company', value: 'workspaceName', width: '220px' },
        { text: 'Contentplan', value: 'contentplanName', width: '240px' },
        { text: 'Price', value: 'price', sortable: false, width: '140px' },
        { text: 'Status', value: 'status', sortable: false, width: '180px' },
        { text: 'Notes', value: 'notes', sortable: false, width: '300px' }
      ],
      paidExtraHeaders: [
        { text: 'Invoice', value: 'invoiceSent', sortable: false, width: '100px' },
        { text: 'Won date', value: 'wonAt', width: '130px' },
        { text: 'Auto sprint', value: 'autoSprint', sortable: false, width: '130px' },
        { text: 'New', value: 'sprintSizeNew', sortable: false, width: '110px' },
        { text: 'Updates', value: 'sprintSizeUpdate', sortable: false, width: '110px' },
        { text: 'Agent writer', value: 'agentWriter', sortable: false, width: '130px' }
      ],
      pipelineExtraHeaders: [
        { text: 'Invoice', value: 'invoiceSent', sortable: false, width: '100px' },
        { text: 'Discount', value: 'discountPercent', sortable: false, width: '130px' },
        { text: 'Follow-up', value: 'followupDate', sortable: false, width: '150px' },
        { text: 'GSC', value: 'hasGscIntegration', sortable: false, width: '90px' },
        { text: 'CMS', value: 'mainCmsIntegration', sortable: false, width: '130px' }
      ],
      lostExtraHeaders: [
        { text: 'Invoice', value: 'invoiceSent', sortable: false, width: '100px' },
        { text: 'Lost date', value: 'lostAt', width: '130px' },
        { text: 'Reason', value: 'lostReason', sortable: false, width: '280px' },
        { text: 'Won date', value: 'wonAt', width: '130px' }
      ],
      workspaceActionHeader: { text: 'Actions', value: 'workspaceActions', sortable: false, width: '280px' }
    }
  },
  computed: {
    ...mapGetters([
      'user'
    ]),
    isAdmin () {
      return !!this.user?.admin
    },
    isPipelineScope () {
      return this.activeClientScopeValue === 'pipeline'
    },
    isLostScope () {
      return this.activeClientScopeValue === 'lost'
    },
    statusItems () {
      if (this.isLostScope) return LOST_STATUS_ITEMS
      return this.isPipelineScope ? PIPELINE_STATUS_ITEMS : PAID_STATUS_ITEMS
    },
    tabs () {
      const rows = this.scopeRows
      if (this.isLostScope) {
        return [
          { text: 'All', value: 'all', count: rows.length },
          { text: 'Lost', value: 'no status', count: rows.filter(row => row.status === 'no status').length },
          { text: 'Paused', value: 'paused', count: rows.filter(row => row.status === 'paused').length }
        ]
      }

      if (this.isPipelineScope) {
        return [
          { text: 'All', value: 'all', count: rows.length },
          { text: 'Lead', value: 'no status', count: rows.filter(row => row.status === 'no status').length },
          { text: 'Hot lead', value: 'in progress', count: rows.filter(row => row.status === 'in progress').length },
          { text: 'Paused', value: 'paused', count: rows.filter(row => row.status === 'paused').length }
        ]
      }

      return [
        { text: 'All', value: 'all', count: rows.length },
        { text: 'No status', value: 'no status', count: rows.filter(row => row.status === 'no status').length },
        { text: 'In progress', value: 'in progress', count: rows.filter(row => row.status === 'in progress').length },
        { text: 'Done', value: 'done', count: rows.filter(row => row.status === 'done').length }
      ]
    },
    activeTabValue () {
      if (typeof this.selectedTab === 'string' && this.tabs.some(tab => tab.value === this.selectedTab)) return this.selectedTab
      if (typeof this.selectedTab === 'number') return this.tabs[this.selectedTab]?.value || 'all'
      return 'all'
    },
    lostRows () {
      return this.rows
        .filter(row => row.clientStatus === 'lost')
        .sort((a, b) => (b.lostAt || '').localeCompare(a.lostAt || ''))
    },
    pipelineRows () {
      return this.rows.filter(row => row.clientStatus === 'pipeline')
    },
    paidRows () {
      return this.rows
        .filter(row => row.clientStatus === 'paid')
        .sort((a, b) => (b.wonAt || '').localeCompare(a.wonAt || ''))
    },
    pausedLostRows () {
      return this.lostRows.filter(row => row.status === 'paused')
    },
    currentMonth () {
      const date = new Date()
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    },
    activeClientScopeValue () {
      if (['paid', 'pipeline', 'lost'].includes(this.selectedClientScope)) return this.selectedClientScope
      if (typeof this.selectedClientScope === 'number') return ['paid', 'pipeline', 'lost'][this.selectedClientScope] || 'paid'
      return 'paid'
    },
    scopeRows () {
      if (this.isLostScope) return this.lostRows
      return this.isPipelineScope ? this.pipelineRows : this.paidRows
    },
    visibleRows () {
      const tabFiltered = this.activeTabValue === 'all'
        ? this.scopeRows
        : this.scopeRows.filter(row => row.status === this.activeTabValue)
      if (!this.nameSearch.trim()) return tabFiltered
      const query = this.nameSearch.trim().toLowerCase()
      return tabFiltered.filter(row => [
        row.workspaceName,
        row.companyName,
        row.contentplanName,
        row.domain
      ].some(value => (value || '').toLowerCase().includes(query)))
    },
    tableHeaders () {
      if (this.isLostScope) return [...this.baseHeaders, ...this.lostExtraHeaders, this.workspaceActionHeader]

      const scopeHeaders = this.isPipelineScope
        ? [...this.baseHeaders, ...this.pipelineExtraHeaders]
        : [...this.baseHeaders, ...this.paidExtraHeaders]

      return [...scopeHeaders, this.workspaceActionHeader]
    },
    mrrTotal () {
      return this.paidRows.reduce((sum, row) => sum + this.getNumericPrice(row.price), 0)
    },
    pipelineTotal () {
      return [...this.pipelineRows, ...this.pausedLostRows].reduce((sum, row) => sum + this.getNumericPrice(row.price), 0)
    },
    lostTotal () {
      return this.lostRows
        .filter(row => row.status !== 'paused' && row.lostAt?.slice(0, 7) === this.currentMonth)
        .reduce((sum, row) => sum + this.getNumericPrice(row.price), 0)
    },
    notesDialogRow () {
      return this.rows.find(row => row.contentplanId === this.notesDialog.rowId) || null
    },
    notesDialogFirstLinePreview () {
      return this.notesPreview(this.notesDialog.draft || '')
    },
    moveDialogTarget () {
      return CLIENT_STATUS_ITEMS.find(item => item.value === this.moveDialog.clientStatus) || null
    },
    moveDialogTitle () {
      return this.moveDialogTarget ? `Move to ${this.moveDialogTarget.text}` : 'Move client'
    },
    moveDialogDateLabel () {
      if (this.moveDialog.clientStatus === 'paid') return 'Won date'
      if (this.moveDialog.clientStatus === 'lost') return 'Lost date'
      return ''
    }
  },
  watch: {
    activeClientScopeValue () {
      this.selectedTab = 'all'
    }
  },
  methods: {
    ...mapActions([
      'adminContentplansOverviewGet',
      'adminContentplansOverviewPost',
      'adminContentplansOverviewPut',
      'updateWorkspace'
    ]),
    async loadData () {
      if (!this.isAdmin) return

      this.isLoading.overview = true
      try {
        const rows = await this.adminContentplansOverviewGet()
        this.rows = Array.isArray(rows) ? rows.map(this.normalizeRow) : []
      } catch (err) {
        console.error(err)
      }
      this.isLoading.overview = false
    },
    normalizeRow (row = {}) {
      const status = STATUS_OPTIONS.includes(row.status) ? row.status : 'no status'
      const notes = row.notes || ''
      const discountPercent = row.discountPercent === null || typeof row.discountPercent === 'undefined' ? null : Number(row.discountPercent)

      return {
        ...row,
        workspaceName: row.workspaceName || row.companyName || '',
        companyName: row.companyName || '',
        domain: row.domain || '',
        clientStatus: row.clientStatus,
        status,
        notes,
        priceInput: this.formatPriceInput(row.price),
        invoiceSent: !!row.invoiceSent,
        discountPercent,
        discountPercentInput: this.formatPercentInput(discountPercent),
        followupDate: row.followupDate || '',
        wonAt: row.wonAt || '',
        lostAt: row.lostAt || '',
        lostReason: row.lostReason || '',
        autoSprint: !!row.autoSprint,
        agentWriter: !!row.agentWriter,
        sprintSizeNew: this.normalizeSprintSize(row.sprintSizeNew),
        sprintSizeUpdate: this.normalizeSprintSize(row.sprintSizeUpdate),
        sprintSizeNewInput: `${this.normalizeSprintSize(row.sprintSizeNew)}`,
        sprintSizeUpdateInput: `${this.normalizeSprintSize(row.sprintSizeUpdate)}`,
        _savedWorkspaceName: row.workspaceName || row.companyName || '',
        _savedStatus: status,
        _savedNotes: notes,
        _savedPrice: row.price === null || typeof row.price === 'undefined' ? null : Number(row.price),
        _savedInvoiceSent: !!row.invoiceSent,
        _savedDiscountPercent: discountPercent,
        _savedFollowupDate: row.followupDate || '',
        _savedWonAt: row.wonAt || '',
        _savedLostAt: row.lostAt || '',
        _savedLostReason: row.lostReason || '',
        _savedAutoSprint: !!row.autoSprint,
        _savedAgentWriter: !!row.agentWriter,
        _savedSprintSizeNew: this.normalizeSprintSize(row.sprintSizeNew),
        _savedSprintSizeUpdate: this.normalizeSprintSize(row.sprintSizeUpdate)
      }
    },
    resetAddTargetDialog () {
      this.addTargetDialog = {
        open: false,
        saving: false,
        companyName: '',
        domain: '',
        mainContacts: '',
        invoiceSent: false,
        discountPercentInput: '',
        followupDate: '',
        priceInput: '',
        notes: ''
      }
    },
    openAddTargetDialog () {
      this.resetAddTargetDialog()
      this.addTargetDialog.open = true
    },
    closeAddTargetDialog () {
      if (this.addTargetDialog.saving) return
      this.resetAddTargetDialog()
    },
    async createSalesTarget () {
      if (this.addTargetDialog.saving) return
      const price = this.parsePriceInput(this.addTargetDialog.priceInput)
      const discount = this.parsePercentInput(this.addTargetDialog.discountPercentInput)

      if (!price.ok || !discount.ok) {
        this.$store.commit('setSnackbar', {
          open: true,
          text: price.message || discount.message,
          type: 'error'
        })
        return
      }

      this.addTargetDialog.saving = true
      try {
        await this.adminContentplansOverviewPost({
          companyName: this.addTargetDialog.companyName.trim(),
          domain: this.addTargetDialog.domain.trim().toLowerCase(),
          mainContacts: this.addTargetDialog.mainContacts.trim(),
          invoiceSent: !!this.addTargetDialog.invoiceSent,
          discountPercent: discount.value,
          followupDate: this.addTargetDialog.followupDate || null,
          price: price.value,
          notes: this.addTargetDialog.notes.trim()
        })
        this.addTargetDialog.saving = false
        this.closeAddTargetDialog()
        this.selectedClientScope = 'pipeline'
        await this.loadData()
        this.$store.commit('setSnackbar', {
          open: true,
          text: 'Sales target added',
          type: 'success'
        })
      } catch (err) {
        console.error(err)
        this.addTargetDialog.saving = false
      }
    },
    normalizeSprintSize (value) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric) || numeric < 0) return 0
      return Math.floor(numeric)
    },
    parseSprintSizeInput (value) {
      const raw = `${value || ''}`.trim()
      if (!raw) return { ok: true, value: 0 }
      const numeric = Number(raw)
      if (!Number.isInteger(numeric)) return { ok: false, message: 'Sprint size must be an integer' }
      if (numeric < 0) return { ok: false, message: 'Sprint size must be 0 or greater' }
      return { ok: true, value: numeric }
    },
    getNumericPrice (price) {
      const numeric = Number(price)
      if (Number.isNaN(numeric)) return 0
      return numeric
    },
    formatMoney (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(value || 0))
    },
    formatDate (value) {
      if (!value) return '—'
      return value
    },
    formatPriceInput (value) {
      if (value === null || typeof value === 'undefined') return ''
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return ''
      return `${numeric}`
    },
    parsePriceInput (value) {
      const raw = `${value || ''}`.trim()
      if (!raw) return { ok: true, value: null }
      const numeric = Number(raw)
      if (Number.isNaN(numeric)) return { ok: false, message: 'Price must be a number' }
      if (numeric < 0) return { ok: false, message: 'Price must be 0 or greater' }
      return { ok: true, value: Math.round(numeric * 100) / 100 }
    },
    formatPercentInput (value) {
      if (value === null || typeof value === 'undefined') return ''
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return ''
      return `${numeric}`
    },
    parsePercentInput (value) {
      const raw = `${value || ''}`.trim()
      if (!raw) return { ok: true, value: null }
      const numeric = Number(raw)
      if (Number.isNaN(numeric)) return { ok: false, message: 'Discount must be a number' }
      if (numeric < 0 || numeric > 100) return { ok: false, message: 'Discount must be between 0 and 100' }
      return { ok: true, value: Math.round(numeric * 100) / 100 }
    },
    todayDate () {
      return new Date().toISOString().slice(0, 10)
    },
    getStatusMeta (status) {
      return this.statusItems.find(item => item.value === status) || this.statusItems[0]
    },
    formatCmsLabel (value) {
      if (!value || value === 'none') return 'None'
      if (value === 'custom-cms') return 'Custom CMS'
      return value.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
    },
    getCmsMeta (value) {
      return CMS_META[value] || CMS_META.none
    },
    notesPreview (notes) {
      const firstLine = (notes || '')
        .split('\n')
        .map(line => line.trim())
        .find(Boolean)

      if (!firstLine) return 'Add notes'

      const plain = firstLine
        .replace(/^[#>\-*\d.)\s]+/, '')
        .replace(/[*_`~]/g, '')
        .trim()

      if (!plain) return 'Add notes'
      if (plain.length <= 90) return plain
      return `${plain.slice(0, 90)}...`
    },
    isSaving (contentplanId) {
      return !!this.savingRows[contentplanId]
    },
    setSaving (contentplanId, isSaving) {
      this.$set(this.savingRows, contentplanId, isSaving)
    },
    isWorkspaceActionRunning (workspaceId) {
      return !!this.workspaceActionLoading[workspaceId]
    },
    setWorkspaceActionRunning (workspaceId, isRunning) {
      this.$set(this.workspaceActionLoading, workspaceId, isRunning)
    },
    openClientMoveDialog (item, clientStatus) {
      if (!item?.workspaceId || !this.isAdmin) return
      if (!CLIENT_STATUS_ITEMS.some(status => status.value === clientStatus)) return
      if (item.clientStatus === clientStatus) return
      this.moveDialog = {
        open: true,
        saving: false,
        row: item,
        clientStatus,
        date: this.todayDate(),
        lostReason: clientStatus === 'lost' ? item.lostReason || '' : ''
      }
    },
    closeClientMoveDialog () {
      if (this.moveDialog.saving) return
      this.moveDialog = {
        open: false,
        saving: false,
        row: null,
        clientStatus: '',
        date: '',
        lostReason: ''
      }
    },
    async runClientMove () {
      const item = this.moveDialog.row
      const clientStatus = this.moveDialog.clientStatus
      if (!item?.workspaceId || !clientStatus) return
      if (this.isWorkspaceActionRunning(item.workspaceId)) return

      this.moveDialog.saving = true
      this.setWorkspaceActionRunning(item.workspaceId, true)
      try {
        const payload = {
          workspaceId: item.workspaceId,
          contentplanId: item.contentplanId,
          action: 'adminMoveContentplanClientStatus',
          clientStatus
        }
        if (clientStatus === 'paid') payload.wonAt = this.moveDialog.date
        if (clientStatus === 'lost') {
          payload.lostAt = this.moveDialog.date
          payload.lostReason = this.moveDialog.lostReason.trim()
        }

        await this.updateWorkspace(payload)
        const target = this.moveDialogTarget?.text || clientStatus
        this.moveDialog.saving = false
        this.closeClientMoveDialog()
        await this.loadData()
        this.$store.commit('setSnackbar', {
          open: true,
          text: `Moved ${item.workspaceName || item.companyName || `workspace #${item.workspaceId}`} to ${target}`,
          type: 'success'
        })
      } catch (err) {
        console.error(err)
        this.moveDialog.saving = false
      }
      this.setWorkspaceActionRunning(item.workspaceId, false)
    },
    async removeWorkspaceAccess (item) {
      if (!item?.workspaceId) return
      if (!this.isAdmin) return
      if (this.isWorkspaceActionRunning(item.workspaceId)) return
      if (!window.confirm('Remove this row from admin? The workspace and contentplan will stay in the database, the workspace will be set to free, and admin access will be removed.')) return

      this.setWorkspaceActionRunning(item.workspaceId, true)
      try {
        await this.updateWorkspace({
          workspaceId: item.workspaceId,
          action: 'adminRemoveWorkspaceAccess'
        })
        await this.loadData()
        this.$store.commit('setSnackbar', {
          open: true,
          text: `Removed ${item.workspaceName || item.companyName || `workspace #${item.workspaceId}`} from admin`,
          type: 'success'
        })
      } catch (err) {
        console.error(err)
      }
      this.setWorkspaceActionRunning(item.workspaceId, false)
    },
    async saveCompanyName (item) {
      if (!item) return
      const updated = await this.updateRowField(item, 'workspaceName', item.workspaceName || '')
      if (!updated) item.workspaceName = item._savedWorkspaceName
    },
    async savePrice (item) {
      if (!item) return

      const parsed = this.parsePriceInput(item.priceInput)
      if (!parsed.ok) {
        item.priceInput = this.formatPriceInput(item._savedPrice)
        this.$store.commit('setSnackbar', {
          open: true,
          text: parsed.message,
          type: 'error'
        })
        return
      }

      item.price = parsed.value
      const updated = await this.updateRowField(item, 'price', parsed.value)
      if (!updated) item.price = item._savedPrice
      item.priceInput = this.formatPriceInput(updated ? item.price : item._savedPrice)
    },
    async saveDiscountPercent (item) {
      if (!item) return

      const parsed = this.parsePercentInput(item.discountPercentInput)
      if (!parsed.ok) {
        item.discountPercentInput = this.formatPercentInput(item._savedDiscountPercent)
        this.$store.commit('setSnackbar', {
          open: true,
          text: parsed.message,
          type: 'error'
        })
        return
      }

      item.discountPercent = parsed.value
      const updated = await this.updateRowField(item, 'discountPercent', parsed.value)
      if (!updated) item.discountPercent = item._savedDiscountPercent
      item.discountPercentInput = this.formatPercentInput(updated ? item.discountPercent : item._savedDiscountPercent)
    },
    async saveFollowupDate (item) {
      if (!item) return
      const updated = await this.updateRowField(item, 'followupDate', item.followupDate || '')
      if (!updated) item.followupDate = item._savedFollowupDate
    },
    async changeInvoiceSent (item) {
      if (!item) return
      const updated = await this.updateRowField(item, 'invoiceSent', !!item.invoiceSent)
      if (!updated) item.invoiceSent = item._savedInvoiceSent
    },
    async changeStatus (item, status) {
      if (!item) return
      if (item.status === status) return
      item.status = status
      await this.updateRowField(item, 'status', status)
    },
    async changeAutoSprint (item) {
      if (!item) return
      const updated = await this.updateRowField(item, 'autoSprint', !!item.autoSprint)
      if (!updated) item.autoSprint = item._savedAutoSprint
    },
    async changeAgentWriter (item) {
      if (!item) return
      const updated = await this.updateRowField(item, 'agentWriter', !!item.agentWriter)
      if (!updated) item.agentWriter = item._savedAgentWriter
    },
    async saveSprintSize (item, field) {
      if (!item) return
      const inputKey = field === 'sprintSizeNew' ? 'sprintSizeNewInput' : 'sprintSizeUpdateInput'
      const savedKey = field === 'sprintSizeNew' ? '_savedSprintSizeNew' : '_savedSprintSizeUpdate'
      const parsed = this.parseSprintSizeInput(item[inputKey])

      if (!parsed.ok) {
        item[inputKey] = `${item[savedKey]}`
        this.$store.commit('setSnackbar', {
          open: true,
          text: parsed.message,
          type: 'error'
        })
        return
      }

      item[field] = parsed.value
      const updated = await this.updateRowField(item, field, parsed.value)
      if (!updated) item[field] = item[savedKey]
      item[inputKey] = `${updated ? item[field] : item[savedKey]}`
    },
    openNotesDialog (item) {
      if (!item) return
      this.notesDialog.rowId = item.contentplanId
      this.notesDialog.draft = item.notes || ''
      this.notesDialog.open = true
    },
    closeNotesDialog () {
      if (this.notesDialog.saving) return
      this.notesDialog.open = false
      this.notesDialog.rowId = null
      this.notesDialog.draft = ''
    },
    async saveNotesDialog () {
      const row = this.notesDialogRow
      if (!row) return
      if (this.notesDialog.saving) return

      this.notesDialog.saving = true
      const updated = await this.updateRowField(row, 'notes', this.notesDialog.draft || '')
      this.notesDialog.saving = false

      if (updated) this.closeNotesDialog()
    },
    async updateRowField (item, field, value) {
      if (!item?.contentplanId) return false
      if (!['status', 'notes', 'price', 'workspaceName', 'companyName', 'invoiceSent', 'discountPercent', 'followupDate', 'autoSprint', 'agentWriter', 'sprintSizeNew', 'sprintSizeUpdate'].includes(field)) return false
      if (this.isSaving(item.contentplanId)) return false

      const savedKeys = {
        autoSprint: '_savedAutoSprint',
        agentWriter: '_savedAgentWriter',
        workspaceName: '_savedWorkspaceName',
        companyName: '_savedCompanyName',
        discountPercent: '_savedDiscountPercent',
        followupDate: '_savedFollowupDate',
        invoiceSent: '_savedInvoiceSent',
        notes: '_savedNotes',
        price: '_savedPrice',
        sprintSizeNew: '_savedSprintSizeNew',
        sprintSizeUpdate: '_savedSprintSizeUpdate',
        status: '_savedStatus'
      }
      const savedKey = savedKeys[field]
      const normalizedValue = this.normalizeRowFieldValue(field, value)
      if (item[savedKey] === normalizedValue) return true

      const previousValue = item[savedKey]
      this.setSaving(item.contentplanId, true)
      try {
        await this.adminContentplansOverviewPut({
          contentplanId: item.contentplanId,
          [field]: normalizedValue
        })
        item[savedKey] = normalizedValue
        item[field] = normalizedValue
        if (field === 'notes') item.notes = normalizedValue
        if (field === 'price') item.price = normalizedValue
        if (field === 'discountPercent') item.discountPercent = normalizedValue
        this.setSaving(item.contentplanId, false)
        return true
      } catch (err) {
        console.error(err)
        item[field] = previousValue
        if (field === 'price') item.priceInput = this.formatPriceInput(previousValue)
        if (field === 'discountPercent') item.discountPercentInput = this.formatPercentInput(previousValue)
        this.setSaving(item.contentplanId, false)
        return false
      }
    },
    normalizeRowFieldValue (field, value) {
      if (field === 'workspaceName' || field === 'companyName' || field === 'notes' || field === 'followupDate') return value || ''
      if (field === 'price') return value === null || typeof value === 'undefined' ? null : Number(value)
      if (field === 'discountPercent') return value === null || typeof value === 'undefined' ? null : Number(value)
      if (field === 'invoiceSent' || field === 'autoSprint' || field === 'agentWriter') return !!value
      if (field === 'sprintSizeNew' || field === 'sprintSizeUpdate') return this.normalizeSprintSize(value)
      return value
    }
  },
  async mounted () {
    document.title = 'Admin | ContentGecko'
    await this.loadData()
  }
}
